import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import editorStore from "../../stores/EditorStore";
import PhotobookSidePanel from "./PhotobookSidePanel";
import PhotobookWorkspace from "./PhotobookWorkspace";
import productSizeStore from "../../stores/ProductSizeStore";
import projectNameStore from "../../stores/ProjectNameStore";
import Header from "../../Components/Header";
import CanvasToolbar from "../Canvas/CanvasToolbar";
import photobookEditorStore from "../../stores/PhotobookEditorStore";
import CartModal from "../../Components/CartModal";
import { useLocation } from "react-router-dom";
import PhotobookPreview from "./PhotobookPreview";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import loadingSpinner from "../../assets/img/Spinner.svg";

const headerLinks = [{ name: "Home", href: "/" }];

interface cartInputs {
  projectName: string;
  productBasePrice: number;
  productId: number;
  canvasId: number;
  productType: string;
}

interface AlbumCreateResponse {
  albumID: number;
  albumName: string;
  albumDescription: string;
  jsonObject: string;
  photosPerPage: number;
  albumPagesCount: number;
  lastUpdated: string;
  dateCreated: string;
}

interface ProjectInformation {
  associatedProductID: number;
  projectName?: string | null;
  productType: string;
  lastModified: string | null;
  productBasePrice: number;
  productImageUrl: string | null;
  productId: number;
  status: string;
}

const PhotobookEditor = () => {
  const [toggleWorkspaceEditor, setToggleWorkspaceEditor] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [albumId, setAlbumId] = useState<number | null>(null);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [cartData, setCartData] = useState<cartInputs | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const albumData = location.state?.albumData as AlbumCreateResponse | null;
  const projectInformation = location.state?.projectDetails as ProjectInformation | null;

  const productDetails = productSizeStore.getSelectedAlbumProductDetails;
  const projectDetails = projectNameStore.getProjectDetails;

  // Edit flow: name lives in projectInformation. New-project flow: name lives in projectNameStore.
  const effectiveAlbumName = projectInformation?.projectName || projectDetails?.projectName || "";
  const effectiveAlbumDesc = projectDetails?.description || "";

  // Load existing album when editing from My Accounts
  useEffect(() => {
    if (!albumData) return;
    setCartData({
      projectName: projectInformation?.projectName || "",
      productBasePrice: projectInformation?.productBasePrice || 0,
      productId: projectInformation?.productId || 0,
      canvasId: albumData.albumID || 0,
      productType: projectInformation?.productType || "",
    });
    setTimeout(async () => {
      try {
        await editorStore.waitLoading();
        const jsonAlbum = JSON.parse(albumData.jsonObject);
        setShowWorkspace(true);
        setToggleWorkspaceEditor(false);
        editorStore.loadJSON(jsonAlbum);
        setAlbumId(albumData.albumID);
      } catch (err) {
        console.error("Error loading album:", err);
      }
    }, 1000);
  }, [albumData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save to localStorage on unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      localStorage.setItem("savedAlbum", JSON.stringify(editorStore.toJSON()));
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Initialize fresh photobook — always runs for new projects
  useEffect(() => {
    if (albumData) return; // editing flow is handled by the albumData effect

    // Clear any stale localStorage from a previous session
    localStorage.removeItem("savedAlbum");
    editorStore.clear();

    const w = productDetails.width || 600;
    const h = productDetails.height || 800;
    editorStore.setSize(w, h);             // set dimensions before adding pages
    for (let i = 0; i < 5; i++) editorStore.addPage();

    setShowWorkspace(true);
    setToggleWorkspaceEditor(false);       // always start with thumbnail strip
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create album record on backend
  useEffect(() => {
    const savedAlbum = localStorage.getItem("savedAlbum");
    if (!savedAlbum && !albumData && productDetails.productId) {
      const json = editorStore.toJSON();
      // Safety: don't create a photobook record with empty/cross-session data
      if (!json.pages || json.pages.length === 0) return;
      const albumBody = {
        albumName: effectiveAlbumName,
        albumDescription: effectiveAlbumDesc,
        albumJsonObject: JSON.stringify(json),
        layout: "custom",
        photosPerPage: "58",
      };
      photobookEditorStore.createPhotobook(productDetails.productId, albumBody).then(response => {
        if (response?.albumID) setAlbumId(response.albumID);
      });
    }
  }, [productDetails.productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-save
  useEffect(() => {
    const savePhotobook = async () => {
      if (!albumId) return;
      const json = editorStore.toJSON();
      // Safety: don't overwrite the backend if editorStore was cleared/destroyed or
      // belongs to a different editor session (Canvas data leaking through stale listener).
      if (!json.pages || json.pages.length === 0) return;
      const albumBody = {
        albumName: effectiveAlbumName,
        albumDescription: effectiveAlbumDesc,
        albumJsonObject: JSON.stringify(json),
        layout: "custom",
        photosPerPage: "58",
      };
      await photobookEditorStore.updatePhotobook(albumId, albumBody).then(response => {
        if (response?.albumID) {
          setAlbumId(response.albumID);
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      });
    };
    const debouncedSave = debounce(savePhotobook, 10000);
    const unsubscribe = editorStore.on("change", debouncedSave);
    return () => { unsubscribe(); debouncedSave.cancel(); };
  }, [albumId, projectDetails, productDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCartData({
      projectName: productDetails.productName || "",
      productBasePrice: productDetails.basePrice || 0,
      productId: productDetails.productId || 0,
      canvasId: albumId || 0,
      productType: productDetails.productType || "",
    });
    if (projectInformation) {
      setCartData({
        projectName: projectInformation.projectName || "",
        productBasePrice: projectInformation.productBasePrice || 0,
        productId: projectInformation.productId || 0,
        canvasId: albumId || 0,
        productType: projectInformation.productType || "",
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddToCart = () => {
    // When editing from My Accounts, productSizeStore is not populated — use projectInformation.
    const productId = projectInformation?.productId || productDetails.productId || 0;
    const productBasePrice = projectInformation?.productBasePrice ?? productDetails.basePrice ?? 0;
    const productType = projectInformation?.productType || productDetails.productType || "";
    const projectName = projectInformation?.projectName || productDetails.productName || "";
    setCartData({
      projectName,
      productBasePrice,
      productId,
      canvasId: albumId || 0,
      productType,
    });
    setIsCartModalOpen(true);
  };

  const handleTogglePreview = () => {
    setIsPreviewVisible(prev => !prev);
  };

  const handleSaveButton = async () => {
    if (!albumId) { console.error("Album ID not available."); return; }
    const albumBody = {
      albumName: effectiveAlbumName,
      albumDescription: effectiveAlbumDesc,
      albumJsonObject: JSON.stringify(editorStore.toJSON()),
      layout: "custom",
      photosPerPage: "58",
    };
    await photobookEditorStore.updatePhotobook(albumId, albumBody).then(response => {
      if (response?.albumID) {
        setAlbumId(response.albumID);
        toast.success("Album updated successfully", {
          position: "top-right",
          autoClose: 2000,
          onClose: () => navigate("/myaccounts", { replace: false }),
        });
      }
    });
  };

  return (
    <div>
      <Header
        cartItemsCount={0}
        title="Project"
        links={headerLinks}
        productName={cartData?.projectName}
        price={cartData?.productBasePrice}
        onAddToCart={handleAddToCart}
        onPreview={handleTogglePreview}
        showPreviewButton={true}
        onShowSuccess={showSuccessMessage}
        onSaveButton={true}
        handleSaveButton={handleSaveButton}
      />
      <CanvasToolbar
        onThumbnailClick={() => { setShowWorkspace(true); setToggleWorkspaceEditor(false); }}
        onPhotobookEditorClick={() => { setShowWorkspace(true); setToggleWorkspaceEditor(true); setIsPreviewVisible(false); }}
        showTools={true}
      />

      {/* Editor layout */}
      <div style={{ display: "flex", width: "100vw", height: "calc(100vh - 112px)", overflow: "hidden" }}>
        <PhotobookSidePanel
          onTemplateApplied={() => {
            // Apply template to current page and open the editor so user sees the result
            setShowWorkspace(true);
            setToggleWorkspaceEditor(true);
          }}
        />
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          {showWorkspace && (
            <PhotobookWorkspace
              toggleWorkspaceEditor={toggleWorkspaceEditor}
              onToggleWorkspace={setToggleWorkspaceEditor}
            />
          )}
        </div>
      </div>

      {isCartModalOpen && cartData && (
        <CartModal cartInputs={cartData} onClose={() => setIsCartModalOpen(false)} />
      )}

      {isLoading && (
        <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(255,255,255,0.75)" }}>
          <img src={loadingSpinner} alt="Loading..." style={{ width: 80, height: 80 }} />
        </div>
      )}

      {isPreviewVisible && <PhotobookPreview onClose={() => setIsPreviewVisible(false)} />}
    </div>
  );
};

export default observer(PhotobookEditor);
